import React, { useEffect, useState } from 'react';
import {
  Table,
  message,
  Button,
  Space,
  Row,
  Col,
  Input,
  Select,
  Tag,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import { PageTitle, StyledCard } from '../common';
import { theme } from '../../theme';

const { Option } = Select;

// Interfaces
interface Retencion {
  id: number;
  descripcion: string;
  porcentaje: number;
  portonelada: number;
  tipo: string;
  minimo_porton: number;
  minimo_pesos: number;
  acumulado_ganancia: number;
  provincia: number | null;
  provincia_nombre: string | null;
  aplicar: string;
  alta: number;
  empresa: number;
  empresa_nombre: string;
}

const ListarRetenciones: React.FC = () => {
  const [retenciones, setRetenciones] = useState<Retencion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    tipo: undefined as string | undefined,
  });

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Cargar retenciones al inicializar
  useEffect(() => {
    if (user?.id_empresa) {
      fetchRetenciones();
    }
  }, [user]);

  // Cargar retenciones cuando cambie la página o el tamaño de página
  useEffect(() => {
    if (user?.id_empresa && (currentPage > 1 || pageSize !== 10)) {
      fetchRetenciones();
    }
  }, [user, currentPage, pageSize]);

  const fetchRetenciones = async () => {
    if (!user?.id_empresa) return;

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();

      // Construir URL base
      const url = `${process.env.REACT_APP_LIQUIDACION_URL}/listar_retenciones/${user.id_empresa}/`;

      // Construir parámetros de consulta
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      // Agregar filtro de tipo si está definido
      if (filtros.tipo) {
        params.tipo = filtros.tipo;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      });

      if (response.data.results) {
        setRetenciones(response.data.results);
        setTotal(response.data.count || 0);
      } else {
        setRetenciones(response.data);
        setTotal(response.data.length);
      }
    } catch (error: any) {
      console.error('Error al cargar retenciones:', error);
      message.error('Error al cargar las retenciones');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    setCurrentPage(1); // Resetear a la primera página
    setHasSearched(true); // Marcar que se ha realizado una búsqueda
    fetchRetenciones();
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      tipo: undefined,
    });
    setCurrentPage(1);
    setHasSearched(false);
    setRetenciones([]);
    setTotal(0);
  };

  const getTipoTag = (tipo: string) => {
    const tipoColors: { [key: string]: string } = {
      'IVA': 'blue',
      'GANANCIA': 'green',
      'RENTAS': 'orange',
      'OTROS': 'purple'
    };
    return <Tag color={tipoColors[tipo] || 'default'}>{tipo}</Tag>;
  };

  const getEstadoTag = (alta: number) => {
    if (alta === 1) {
      return <Tag color="green">Activo</Tag>;
    } else {
      return <Tag color="red">Inactivo</Tag>;
    }
  };

  const getAplicarTag = (aplicar: string) => {
    const aplicarColors: { [key: string]: string } = {
      'COMPRA/SERVICIO': 'blue',
      'COMPRA': 'green',
      'SERVICIO': 'orange'
    };
    return <Tag color={aplicarColors[aplicar] || 'default'}>{aplicar}</Tag>;
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '$ 0,00';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(numValue);
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '0';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR').format(numValue);
  };

  const formatPercentage = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '0%';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return `${numValue.toFixed(2)}%`;
  };

  const columns: ColumnsType<Retencion> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      align: 'center',
      render: (value) => getTipoTag(value),
      filters: [
        { text: 'IVA', value: 'IVA' },
        { text: 'GANANCIA', value: 'GANANCIA' },
        { text: 'RENTAS', value: 'RENTAS' },
        { text: 'OTROS', value: 'OTROS' },
      ],
      onFilter: (value, record) => record.tipo === value,
    },
    {
      title: 'Porcentaje',
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      width: 100,
      align: 'right',
      render: (value) => formatPercentage(value),
      sorter: (a, b) => a.porcentaje - b.porcentaje,
    },
    {
      title: 'Portón/Tonelada',
      dataIndex: 'portonelada',
      key: 'portonelada',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
      sorter: (a, b) => a.portonelada - b.portonelada,
    },
    {
      title: 'Mín. Portón',
      dataIndex: 'minimo_porton',
      key: 'minimo_porton',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
      sorter: (a, b) => a.minimo_porton - b.minimo_porton,
    },
    {
      title: 'Mín. Pesos',
      dataIndex: 'minimo_pesos',
      key: 'minimo_pesos',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
      sorter: (a, b) => a.minimo_pesos - b.minimo_pesos,
    },
    {
      title: 'Acum. Ganancia',
      dataIndex: 'acumulado_ganancia',
      key: 'acumulado_ganancia',
      width: 140,
      align: 'right',
      render: (value) => formatCurrency(value),
      sorter: (a, b) => a.acumulado_ganancia - b.acumulado_ganancia,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia_nombre',
      key: 'provincia_nombre',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: 'Aplicar a',
      dataIndex: 'aplicar',
      key: 'aplicar',
      width: 120,
      align: 'center',
      render: (value) => getAplicarTag(value),
      filters: [
        { text: 'COMPRA/SERVICIO', value: 'COMPRA/SERVICIO' },
        { text: 'COMPRA', value: 'COMPRA' },
        { text: 'SERVICIO', value: 'SERVICIO' },
      ],
      onFilter: (value, record) => record.aplicar === value,
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'alta',
      width: 80,
      align: 'center',
      render: (value) => getEstadoTag(value),
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 },
      ],
      onFilter: (value, record) => record.alta === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 100,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditar(record.id)}
            />
          </Tooltip>
          {/* Botón Eliminar deshabilitado - funcionalidad no disponible */}
          {/* <Tooltip title="Eliminar (no disponible)">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleEliminar(record)}
              disabled
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Funciones para los botones
  const handleNuevaRetencion = () => {
    navigate('/liquidacion/alta_retenciones');
  };

  const handleEditar = (retencionId: number) => {
    navigate(`/liquidacion/editar_retenciones/${retencionId}`);
  };

  const handleEliminar = (record: Retencion) => {
    message.info(`Eliminación de retención ID: ${record.id} - Funcionalidad en desarrollo`);
  };

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Listado de Retenciones/Percepciones</PageTitle>

      {/* Botón Nueva Retención */}
      <div style={{ marginBottom: '20px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleNuevaRetencion}
          size="large"
          style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary }}
        >
          Nueva Retención
        </Button>
      </div>

      {/* Filtros */}
      <StyledCard title="Filtros de Búsqueda" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
              Tipo de Retención:
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar tipo"
              value={filtros.tipo}
              onChange={(value) => setFiltros(prev => ({ ...prev, tipo: value }))}
              allowClear
            >
              <Option value="IVA">IVA</Option>
              <Option value="GANANCIA">GANANCIA</Option>
              <Option value="RENTAS">RENTAS</Option>
              <Option value="OTROS">OTROS</Option>
            </Select>
          </Col>

          <Col xs={24} sm={24} md={8} lg={12}>
            <Space style={{ marginTop: '24px' }}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleBuscar}
                loading={loading}
              >
                Buscar
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleLimpiarFiltros}
              >
                Limpiar
              </Button>
            </Space>
          </Col>
        </Row>
      </StyledCard>

      {/* Tabla */}
      {!hasSearched ? (
        <StyledCard darkHeader={false}>
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: theme.colors.textSecondary
          }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>
              🔍 Configure los filtros y haga clic en "Buscar" para ver las retenciones
            </div>
            <div style={{ fontSize: '14px' }}>
              Puede filtrar por tipo de retención o ver todas las retenciones disponibles
            </div>
          </div>
        </StyledCard>
      ) : (
        <StyledCard title="Retenciones/Percepciones" bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns}
            dataSource={retenciones}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} retenciones`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            size="small"
            bordered
            locale={{
              emptyText: 'No se encontraron retenciones con los filtros seleccionados'
            }}
          />
        </StyledCard>
      )}
    </div>
  );
};

export default ListarRetenciones;
